/* Shared presentational pieces for the PCIS dashboard (Overview / Couriers /
   Failures / Lifecycle / Orders).

   Palette continues the one PcisHealthSummary established — teal = success,
   coral = failure, indigo = neutral/total, amber = warning, violet = secondary
   series — so the new dashboard reads as the same product as the pages beside it.

   Everything here is presentational: no data fetching, no filter state. */
import React from 'react'
import PropTypes from 'prop-types'
import {Box, Stack, Tooltip, Typography} from '@mui/material'

/* {main, soft(bg tint), g1→g2 gradient} — same shape as PcisHealthSummary's. */
export const TEAL = {main: '#0EA5A4', soft: '#E6FBF8', g1: '#2DD4BF', g2: '#0EA5A4'}
export const CORAL = {main: '#F43F5E', soft: '#FFECEF', g1: '#FB7185', g2: '#F43F5E'}
export const INDIGO = {main: '#6366F1', soft: '#EEF0FE', g1: '#818CF8', g2: '#6366F1'}
export const AMBER = {main: '#F59E0B', soft: '#FEF3E2', g1: '#FBBF24', g2: '#F59E0B'}
export const VIOLET = {main: '#8B5CF6', soft: '#F1EBFE', g1: '#A78BFA', g2: '#8B5CF6'}
export const SLATE = {main: '#64748B', soft: '#F1F5F9', g1: '#94A3B8', g2: '#64748B'}

/* Failure-rate bands, shared by the health pill, the donut and every rate cell so
   "amber" means the same thing everywhere on the page. */
export const rateBand = (rate) =>
  rate >= 20 ? {label: 'Critical', c: CORAL} : rate >= 5 ? {label: 'Degraded', c: AMBER} : {label: 'Healthy', c: TEAL}

/* ── formatters ──────────────────────────────────────────────────────────── */

export const nf = (n) => (n == null ? '—' : Number(n).toLocaleString('en-IN'))

/* Compact form for chart axes and tight cells: 1.2L / 34.5k / 920. Indian units,
   since every other number on this screen is en-IN. */
export const compact = (n) => {
  if (n == null) return '—'
  const v = Number(n)
  if (Math.abs(v) >= 1e7) return `${(v / 1e7).toFixed(1)}Cr`
  if (Math.abs(v) >= 1e5) return `${(v / 1e5).toFixed(1)}L`
  if (Math.abs(v) >= 1e3) return `${(v / 1e3).toFixed(1)}k`
  return String(v)
}

export const pct = (n, digits = 1) => (n == null ? '—' : `${Number(n).toFixed(digits)}%`)

export const money = (v) => (v == null ? '—' : `₹${Number(v).toLocaleString('en-IN', {maximumFractionDigits: 0})}`)

/* Collapse repeat failures on the same reference number (retries against the
   same courier) down to just the latest attempt, so one order doesn't read as
   several. Keyed on (referenceNumber, courierCode) — a retry against a
   different courier is a different shipment, not a duplicate. */
export const dedupeLatestByRef = (rows = []) => {
  const latest = new Map()
  rows.forEach((r) => {
    const key = `${r.referenceNumber || ''}::${r.courierCode || ''}`
    const prev = latest.get(key)
    if (!prev || new Date(r.createdAt) > new Date(prev.createdAt)) latest.set(key, r)
  })
  return rows.filter((r) => latest.get(`${r.referenceNumber || ''}::${r.courierCode || ''}`) === r)
}

/* Latency reads better as ms below a second and as seconds above it. */
export const ms = (v) => {
  if (v == null) return '—'
  const n = Number(v)
  return n >= 1000 ? `${(n / 1000).toFixed(2)}s` : `${Math.round(n)}ms`
}

/* API bucket labels are 'yyyy-MM-dd' or 'yyyy-MM-dd HH:00:00'. */
export const fmtBucket = (label, bucket) => {
  if (!label) return ''
  const d = new Date(label.replace(' ', 'T'))
  if (isNaN(d)) return label
  return bucket === 'HOUR'
    ? d.toLocaleString('en-IN', {day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'})
    : d.toLocaleDateString('en-IN', {day: '2-digit', month: 'short'})
}

/* ── Panel: the standard card every widget sits in ───────────────────────── */

export function Panel({title, note, action, children, sx}) {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: '14px',
        bgcolor: 'background.paper',
        boxShadow: '0 1px 2px rgba(15,23,42,.04)',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        ...sx,
      }}
    >
      {title ? (
        <Stack direction='row' alignItems='baseline' justifyContent='space-between' spacing={1.5} sx={{px: 2.25, pt: 1.75}}>
          <Box sx={{minWidth: 0}}>
            <Typography sx={{fontSize: 14, fontWeight: 750, letterSpacing: '-.01em'}}>{title}</Typography>
            {note ? <Typography sx={{fontSize: 11.5, color: 'text.secondary', mt: 0.25}}>{note}</Typography> : null}
          </Box>
          {action}
        </Stack>
      ) : null}
      <Box sx={{px: 2.25, py: 2, flex: 1, minWidth: 0}}>{children}</Box>
    </Box>
  )
}
Panel.propTypes = {title: PropTypes.node, note: PropTypes.node, action: PropTypes.node, children: PropTypes.node, sx: PropTypes.object}

/* ── Delta chip: movement vs the preceding window ────────────────────────── */

/* `goodWhenUp` flips the colour semantics — volume rising is good, failure rate
   rising is not. Renders nothing when the API returned null (no baseline). */
export function Delta({value, unit = '%', goodWhenUp = true, digits = 1}) {
  if (value == null) return null
  const up = value > 0
  const flat = Math.abs(value) < 0.005
  const good = flat ? null : up === goodWhenUp
  const palette = good === null ? SLATE : good ? TEAL : CORAL
  const sign = flat ? '' : up ? '+' : '−'
  return (
    <Box
      component='span'
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: '5px',
        py: '1px',
        borderRadius: '4px',
        fontSize: 11,
        fontWeight: 700,
        bgcolor: palette.soft,
        color: palette.main,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {sign}
      {Math.abs(value).toFixed(digits)}
      {unit}
    </Box>
  )
}
Delta.propTypes = {value: PropTypes.number, unit: PropTypes.string, goodWhenUp: PropTypes.bool, digits: PropTypes.number}

/* ── Stat: one KPI ───────────────────────────────────────────────────────── */

export function Stat({label, value, unit, sub, delta, palette = INDIGO, share}) {
  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '14px',
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        p: 2,
        pl: 2.25,
        boxShadow: '0 1px 2px rgba(15,23,42,.04)',
        transition: 'transform .18s ease, box-shadow .18s ease',
        '&::before': {content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, bgcolor: palette.main},
        '&:hover': {transform: 'translateY(-2px)', boxShadow: `0 10px 24px ${palette.main}22`},
      }}
    >
      <Typography
        sx={{fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: 'text.secondary'}}
      >
        {label}
      </Typography>

      <Typography sx={{fontSize: 28, fontWeight: 800, lineHeight: 1.12, letterSpacing: '-.03em', mt: 0.75, fontVariantNumeric: 'tabular-nums'}}>
        {value}
        {unit ? (
          <Box component='span' sx={{fontSize: 14, fontWeight: 700, color: palette.main, ml: 0.4}}>
            {unit}
          </Box>
        ) : null}
      </Typography>

      {share != null ? (
        <Box sx={{height: 5, borderRadius: '999px', bgcolor: 'action.hover', overflow: 'hidden', mt: 1.25}}>
          <Box
            sx={{
              height: '100%',
              width: `${Math.max(0, Math.min(100, share))}%`,
              borderRadius: '999px',
              backgroundImage: `linear-gradient(90deg, ${palette.g1}, ${palette.g2})`,
              transition: 'width .35s ease',
            }}
          />
        </Box>
      ) : null}

      {sub || delta ? (
        <Stack direction='row' alignItems='center' spacing={0.75} sx={{mt: 0.75, flexWrap: 'wrap'}}>
          {delta}
          {sub ? <Typography sx={{fontSize: 11.5, color: 'text.secondary'}}>{sub}</Typography> : null}
        </Stack>
      ) : null}
    </Box>
  )
}
Stat.propTypes = {
  label: PropTypes.node,
  value: PropTypes.node,
  unit: PropTypes.node,
  sub: PropTypes.node,
  delta: PropTypes.node,
  palette: PropTypes.object,
  share: PropTypes.number,
}

/* Responsive KPI grid — the wrapper every tab uses for its Stat row. */
export function StatGrid({children, min = 190, sx}) {
  return (
    <Box sx={{display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`, gap: 2, ...sx}}>
      {children}
    </Box>
  )
}
StatGrid.propTypes = {children: PropTypes.node, min: PropTypes.number, sx: PropTypes.object}

/* ── Pill: a status/severity chip ────────────────────────────────────────── */

export function Pill({children, palette = SLATE, title}) {
  const chip = (
    <Box
      component='span'
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: '7px',
        py: '2px',
        borderRadius: '5px',
        fontSize: 10.5,
        fontWeight: 750,
        letterSpacing: '.02em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        bgcolor: palette.soft,
        color: palette.main,
      }}
    >
      {children}
    </Box>
  )
  return title ? <Tooltip title={title}>{chip}</Tooltip> : chip
}
Pill.propTypes = {children: PropTypes.node, palette: PropTypes.object, title: PropTypes.node}

/* ── Meter: an inline proportion bar ─────────────────────────────────────── */

export function Meter({value, palette = INDIGO, height = 6, sx}) {
  return (
    <Box sx={{height, borderRadius: '999px', bgcolor: 'action.hover', overflow: 'hidden', minWidth: 60, ...sx}}>
      <Box
        sx={{
          height: '100%',
          width: `${Math.max(0, Math.min(100, value || 0))}%`,
          borderRadius: '999px',
          backgroundImage: `linear-gradient(90deg, ${palette.g1}, ${palette.g2})`,
          transition: 'width .35s ease',
        }}
      />
    </Box>
  )
}
Meter.propTypes = {value: PropTypes.number, palette: PropTypes.object, height: PropTypes.number, sx: PropTypes.object}

/* ── LabelledBar: label + meter + value, the workhorse list row ──────────── */

export function LabelledBar({label, sublabel, value, share, palette = INDIGO, labelWidth = 150}) {
  return (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25}}>
      <Box sx={{width: labelWidth, flexShrink: 0, minWidth: 0}}>
        <Typography sx={{fontSize: 12.5, fontWeight: 600, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
          {label}
        </Typography>
        {sublabel ? (
          <Typography sx={{fontSize: 10.5, color: 'text.secondary', lineHeight: 1.2}}>{sublabel}</Typography>
        ) : null}
      </Box>
      <Meter value={share} palette={palette} height={8} sx={{flex: 1}} />
      <Typography sx={{fontSize: 12, fontWeight: 700, width: 62, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>
        {value}
      </Typography>
    </Box>
  )
}
LabelledBar.propTypes = {
  label: PropTypes.node,
  sublabel: PropTypes.node,
  value: PropTypes.node,
  share: PropTypes.number,
  palette: PropTypes.object,
  labelWidth: PropTypes.number,
}

/* ── SplitLabelledBar: same row, but the bar itself is success/fail segments ─
   Used where the split matters more than a single rate-based tint — e.g.
   Volume by Courier, where "half the bar is red" should mean "half the
   volume failed," not "this courier's overall rate falls in the red band." */

export function SplitLabelledBar({label, sublabel, value, successPct, failPct, labelWidth = 150, successPalette = TEAL, failPalette = CORAL}) {
  const success = Math.max(0, Math.min(100, successPct || 0))
  const fail = Math.max(0, Math.min(100 - success, failPct || 0))
  return (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25}}>
      <Box sx={{width: labelWidth, flexShrink: 0, minWidth: 0}}>
        <Typography sx={{fontSize: 12.5, fontWeight: 600, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
          {label}
        </Typography>
        {sublabel ? (
          <Typography sx={{fontSize: 10.5, color: 'text.secondary', lineHeight: 1.2}}>{sublabel}</Typography>
        ) : null}
      </Box>
      <Box sx={{flex: 1, height: 8, borderRadius: '999px', bgcolor: 'action.hover', overflow: 'hidden', display: 'flex', minWidth: 60}}>
        <Box sx={{height: '100%', width: `${success}%`, backgroundImage: `linear-gradient(90deg, ${successPalette.g1}, ${successPalette.g2})`, transition: 'width .35s ease'}} />
        <Box sx={{height: '100%', width: `${fail}%`, backgroundImage: `linear-gradient(90deg, ${failPalette.g1}, ${failPalette.g2})`, transition: 'width .35s ease'}} />
      </Box>
      <Typography sx={{fontSize: 12, fontWeight: 700, width: 62, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>
        {value}
      </Typography>
    </Box>
  )
}
SplitLabelledBar.propTypes = {
  label: PropTypes.node,
  sublabel: PropTypes.node,
  value: PropTypes.node,
  successPct: PropTypes.number,
  failPct: PropTypes.number,
  labelWidth: PropTypes.number,
  successPalette: PropTypes.object,
  failPalette: PropTypes.object,
}

/* ── Callout: a short "this needs attention" line ────────────────────────── */

export function Callout({tone = AMBER, icon = '⚠', title, children}) {
  return (
    <Stack
      direction='row'
      spacing={1.25}
      sx={{border: 1, borderColor: 'divider', borderStyle: 'dashed', borderRadius: '12px', p: 1.5, bgcolor: `${tone.main}0F`}}
    >
      <Box sx={{fontSize: 14, color: tone.main, lineHeight: 1.4}}>{icon}</Box>
      <Typography sx={{fontSize: 12.5, color: 'text.secondary', lineHeight: 1.5}}>
        <Box component='span' sx={{fontWeight: 750, color: 'text.primary'}}>
          {title}
        </Box>
        {title ? ' — ' : ''}
        {children}
      </Typography>
    </Stack>
  )
}
Callout.propTypes = {tone: PropTypes.object, icon: PropTypes.node, title: PropTypes.node, children: PropTypes.node}

/* ── Empty: the "nothing here" state, used instead of a blank card ───────── */

export function Empty({children = 'No data for this window and filter set.'}) {
  return (
    <Box sx={{py: 4, textAlign: 'center', color: 'text.disabled', fontSize: 12.5}}>{children}</Box>
  )
}
Empty.propTypes = {children: PropTypes.node}

/* ── Compact data table ──────────────────────────────────────────────────── */

/* A plain table, not ProzoDataGrid: these are short, non-paginated, read-only
   rollups where the grid's virtualisation and toolbar are pure overhead.
   columns: [{key, header, align?, width?, render(row)}] */
export function MiniTable({columns, rows, getKey, empty}) {
  if (!rows || rows.length === 0) return <Empty>{empty}</Empty>
  return (
    <Box sx={{overflowX: 'auto'}}>
      <Box component='table' sx={{width: '100%', borderCollapse: 'collapse', fontSize: 12.5}}>
        <Box component='thead'>
          <Box component='tr'>
            {columns.map((c) => (
              <Box
                component='th'
                key={c.key}
                sx={{
                  textAlign: c.align || 'left',
                  fontSize: 9.5,
                  fontWeight: 700,
                  letterSpacing: '.08em',
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  pb: 1,
                  px: 1,
                  whiteSpace: 'nowrap',
                  borderBottom: 1,
                  borderColor: 'divider',
                  width: c.width,
                }}
              >
                {c.header}
              </Box>
            ))}
          </Box>
        </Box>
        <Box component='tbody'>
          {rows.map((r, i) => (
            <Box
              component='tr'
              key={getKey ? getKey(r, i) : i}
              sx={{'&:hover': {bgcolor: 'action.hover'}}}
            >
              {columns.map((c) => (
                <Box
                  component='td'
                  key={c.key}
                  sx={{
                    px: 1,
                    py: 1.15,
                    textAlign: c.align || 'left',
                    borderBottom: i === rows.length - 1 ? 0 : '1px solid',
                    borderColor: 'divider',
                    verticalAlign: 'middle',
                    fontVariantNumeric: c.align === 'right' ? 'tabular-nums' : undefined,
                  }}
                >
                  {c.render ? c.render(r) : r[c.key]}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  )
}
MiniTable.propTypes = {columns: PropTypes.array.isRequired, rows: PropTypes.array, getKey: PropTypes.func, empty: PropTypes.node}
